import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";

import { BannerCharCounter } from "./BannerCharCounter";
import {
  ALLOWED_LINK_PROTOCOLS_MESSAGE,
  clampToRemaining,
  isAllowedRichTextHref,
  sanitizeBannerRichText,
} from "./bannerRichText";

/**
 * BN-010 — Editor WYSIWYG del cuerpo de la landing.
 *
 * Toolbar fija de tres comandos (negrita, cursiva, link), como pide la spec §3.4; las listas
 * quedan explícitamente fuera de v1. Es `contentEditable` propio y no una librería: el repo no
 * tiene ninguna de rich text y sumar una (quill y sus ~40 KB gz) por tres botones no se paga.
 *
 * Dos reglas gobiernan todo lo demás:
 *
 * 1. El DOM es la superficie de edición, el string saneado es el valor. Nunca se reescribe el
 *    `innerHTML` mientras se tipea — el saneado cambia el markup del navegador casi siempre
 *    (Chrome arma `<div>`, nosotros guardamos `<p>`) y reescribirlo mandaría el caret al
 *    principio en cada tecla. El `innerHTML` solo se toca cuando el valor cambia DESDE AFUERA.
 * 2. El límite es duro y se aplica ANTES de que el caracter entre (`beforeinput`), no después
 *    con un error. El pegado se intercepta aparte porque no pasa por `beforeinput` con la data
 *    completa en todos los navegadores, y de paso entra como texto plano recortado.
 */
interface BannerRichTextEditorProps {
  id: string;
  /** HTML saneado. */
  value: string;
  onChange: (html: string) => void;
  onBlur?: () => void;
  maxLength: number;
  disabled?: boolean;
  invalid?: boolean;
  placeholder?: string;
}

/** jsdom no implementa `execCommand`/`queryCommandState`: sin esto los tests del form explotan. */
const canRunCommand = (name: "execCommand" | "queryCommandState"): boolean =>
  typeof document !== "undefined" && typeof (document as Document)[name] === "function";

export const BannerRichTextEditor = ({
  id,
  value,
  onChange,
  onBlur,
  maxLength,
  disabled = false,
  invalid = false,
  placeholder,
}: BannerRichTextEditorProps) => {
  const editorRef = useRef<HTMLDivElement>(null);
  /** Lo último que emitimos hacia afuera, para distinguir un cambio externo de nuestro propio eco. */
  const emittedRef = useRef(value);
  /**
   * Se sanea ANTES de montarlo. El valor puede venir de la API sin haber pasado nunca por este
   * editor (cargado por otra version del BO, o por la API directo), y este es el unico lugar
   * donde el HTML entra al DOM vivo del Backoffice: un `<img onerror>` guardado en `body`
   * correria con la sesion del admin. La vista previa ya saneaba; faltaba aca.
   */
  const initialHtmlRef = useRef(sanitizeBannerRichText(value));
  const savedRangeRef = useRef<Range | null>(null);

  const [length, setLength] = useState(0);
  const [marks, setMarks] = useState({ bold: false, italic: false });
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkText, setLinkText] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [linkError, setLinkError] = useState<string>();

  const plainLength = (): number => editorRef.current?.textContent?.length ?? 0;

  /**
   * Cuantos caracteres del editor va a reemplazar lo que se escriba.
   *
   * Se exigen los DOS extremos adentro: con solo el ancla, una seleccion que empieza en el editor
   * y termina en la vista previa devolvia `toString()` de TODO lo seleccionado y regalaba
   * presupuesto. Y se descuentan los saltos de linea, que `toString()` agrega en los bordes de
   * bloque y `textContent` no cuenta: el presupuesto tiene que quedar corto, nunca largo.
   */
  const selectionLength = (): number => {
    const selection = window.getSelection();
    const element = editorRef.current;
    if (!selection || selection.rangeCount === 0 || !element) return 0;
    if (!element.contains(selection.anchorNode) || !element.contains(selection.focusNode)) return 0;
    return selection.toString().replace(/\n/g, "").length;
  };

  /** Cuántos caracteres entran todavía, contando que lo seleccionado se reemplaza. */
  const remainingBudget = (): number => maxLength - plainLength() + selectionLength();

  const emit = useCallback(() => {
    const element = editorRef.current;
    if (!element) return;
    const html = sanitizeBannerRichText(element.innerHTML);
    emittedRef.current = html;
    setLength(element.textContent?.length ?? 0);
    onChange(html);
  }, [onChange]);

  // Sincroniza SOLO cambios de afuera (carga del banner, reset del form, descarte del CTA).
  useEffect(() => {
    const element = editorRef.current;
    if (!element || value === emittedRef.current) return;
    element.innerHTML = sanitizeBannerRichText(value);
    emittedRef.current = value;
    setLength(element.textContent?.length ?? 0);
  }, [value]);

  useEffect(() => {
    setLength(editorRef.current?.textContent?.length ?? 0);
  }, []);

  // `beforeinput` y `paste` van como listeners nativos: el `onBeforeInput` sintético de React no
  // expone `data` de forma consistente, y acá hace falta saber cuántos caracteres entrarían.
  useEffect(() => {
    const element = editorRef.current;
    if (!element || disabled) return;

    const handleBeforeInput = (event: Event): void => {
      const inputEvent = event as InputEvent;
      // `insertFromDrop` no trae el texto en `data` sino en `dataTransfer`: sin esto, arrastrar
      // una seleccion al editor saltaba el limite duro y solo fallaba al guardar.
      const incoming = inputEvent.data ?? inputEvent.dataTransfer?.getData("text/plain") ?? "";
      if (!incoming) return; // borrar, formatear o mover no suma caracteres
      if (incoming.length > remainingBudget()) event.preventDefault();
    };

    const handlePaste = (event: ClipboardEvent): void => {
      event.preventDefault();
      const text = clampToRemaining(
        event.clipboardData?.getData("text/plain") ?? "",
        remainingBudget()
      );
      if (text && canRunCommand("execCommand")) document.execCommand("insertText", false, text);
      emit();
    };

    element.addEventListener("beforeinput", handleBeforeInput);
    element.addEventListener("paste", handlePaste as EventListener);
    return () => {
      element.removeEventListener("beforeinput", handleBeforeInput);
      element.removeEventListener("paste", handlePaste as EventListener);
    };
    // `remainingBudget` lee refs y props en el momento de la llamada, no hace falta re-suscribir.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled, emit, maxLength]);

  useEffect(() => {
    if (!canRunCommand("queryCommandState")) return;
    const syncMarks = (): void => {
      const anchor = document.getSelection()?.anchorNode ?? null;
      if (!anchor || !editorRef.current?.contains(anchor)) return;
      setMarks({
        bold: document.queryCommandState("bold"),
        italic: document.queryCommandState("italic"),
      });
    };
    document.addEventListener("selectionchange", syncMarks);
    return () => document.removeEventListener("selectionchange", syncMarks);
  }, []);

  const applyMark = (command: "bold" | "italic"): void => {
    if (disabled || !canRunCommand("execCommand")) return;
    editorRef.current?.focus();
    // Sin esto Chrome envuelve en `<span style="font-weight:bold">`, que el saneado tira.
    document.execCommand("styleWithCSS", false, "false");
    document.execCommand(command);
    emit();
  };

  const openLinkPanel = (): void => {
    if (disabled) return;
    const selection = window.getSelection();
    const range = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
    const insideEditor = !!range && !!editorRef.current?.contains(range.commonAncestorContainer);
    savedRangeRef.current = insideEditor ? range.cloneRange() : null;
    setLinkText(insideEditor ? range.toString() : "");
    setLinkUrl("");
    setLinkError(undefined);
    setLinkOpen(true);
  };

  const insertLink = (): void => {
    const element = editorRef.current;
    if (!element) return;

    const url = linkUrl.trim();
    if (!isAllowedRichTextHref(url)) {
      setLinkError(ALLOWED_LINK_PROTOCOLS_MESSAGE);
      return;
    }

    const text = linkText.trim() || url;
    const replaced = savedRangeRef.current?.toString().length ?? 0;
    const remaining = maxLength - plainLength() + replaced;
    if (text.length > remaining) {
      setLinkError(`El texto del link no entra: quedan ${Math.max(0, remaining)} caracteres`);
      return;
    }

    element.focus();
    const selection = window.getSelection();
    const range = savedRangeRef.current ?? document.createRange();
    if (!savedRangeRef.current) {
      range.selectNodeContents(element);
      range.collapse(false);
    }
    selection?.removeAllRanges();
    selection?.addRange(range);
    range.deleteContents();

    const anchor = document.createElement("a");
    anchor.setAttribute("href", url);
    anchor.textContent = text;
    range.insertNode(anchor);
    range.setStartAfter(anchor);
    range.collapse(true);
    selection?.removeAllRanges();
    selection?.addRange(range);

    savedRangeRef.current = null;
    setLinkOpen(false);
    emit();
  };

  const atLimit = length >= maxLength;

  return (
    <div className={`banner-rte${invalid ? " banner-rte--invalid" : ""}`}>
      <div className="banner-rte__toolbar" role="toolbar" aria-label="Formato del contenido">
        {/* PrimeIcons no tiene `pi-bold` ni `pi-italic` (lo verifiqué contra
            node_modules/primeicons/primeicons.css): con `icon` esos dos botones salen VACÍOS. Van
            con la letra, que además es la convención universal de toolbar. La forma es la misma
            que la de los íconos de acción del resto del BO — `rounded outlined size="small"`,
            igual que <RowActions> (SYSTEM_DESIGN §6.2) — para que la toolbar no se lea como un
            control de otra app. */}
        <Button
          type="button"
          label="B"
          rounded
          outlined
          size="small"
          aria-label="Negrita"
          aria-pressed={marks.bold}
          className={`banner-rte__mark banner-rte__mark--bold${marks.bold ? " banner-rte__mark--on" : ""}`}
          disabled={disabled}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => applyMark("bold")}
        />
        <Button
          type="button"
          label="I"
          rounded
          outlined
          size="small"
          aria-label="Cursiva"
          aria-pressed={marks.italic}
          className={`banner-rte__mark banner-rte__mark--italic${marks.italic ? " banner-rte__mark--on" : ""}`}
          disabled={disabled}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => applyMark("italic")}
        />
        <Button
          type="button"
          icon="pi pi-link"
          rounded
          outlined
          size="small"
          aria-label="Insertar link"
          aria-expanded={linkOpen}
          className="banner-rte__mark"
          disabled={disabled}
          onMouseDown={(event) => event.preventDefault()}
          onClick={openLinkPanel}
        />
      </div>

      {linkOpen && (
        <div className="banner-rte__link-panel" role="group" aria-label="Insertar link">
          <div className="banner-rte__link-fields">
            <label className="text-sm text-600" htmlFor={`${id}-link-text`}>
              Texto del link
              <InputText
                id={`${id}-link-text`}
                value={linkText}
                onChange={(event) => setLinkText(event.target.value)}
              />
            </label>
            <label className="text-sm text-600" htmlFor={`${id}-link-url`}>
              URL
              <InputText
                id={`${id}-link-url`}
                value={linkUrl}
                placeholder="https://"
                onChange={(event) => {
                  setLinkUrl(event.target.value);
                  setLinkError(undefined);
                }}
              />
            </label>
          </div>
          {/* SYSTEM_DESIGN §6.3: primario primero, "Cancelar" después. */}
          <div className="banner-rte__link-actions">
            <Button type="button" label="Insertar" size="small" onClick={insertLink} />
            <Button
              type="button"
              label="Cancelar"
              size="small"
              outlined
              severity="secondary"
              onClick={() => {
                savedRangeRef.current = null;
                setLinkOpen(false);
              }}
            />
          </div>
          {linkError && (
            <small style={{ color: "var(--error-default)" }} role="alert">
              {linkError}
            </small>
          )}
        </div>
      )}

      <div
        id={id}
        ref={editorRef}
        className={`banner-rte__area${length === 0 ? " banner-rte__area--empty" : ""}`}
        role="textbox"
        aria-multiline="true"
        aria-label="Cuerpo de la landing"
        data-placeholder={placeholder}
        contentEditable={!disabled}
        suppressContentEditableWarning
        onInput={emit}
        onBlur={onBlur}
        dangerouslySetInnerHTML={{ __html: initialHtmlRef.current }}
      />

      <div className="banner-rte__footer">
        {atLimit && (
          <small style={{ color: "var(--warning-default)" }} role="status">
            Llegaste al máximo de {maxLength} caracteres
          </small>
        )}
        <BannerCharCounter value={length} max={maxLength} label="cuerpo de la landing" />
      </div>
    </div>
  );
};
